import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import { Tooltip, IconButton } from "@mui/material";
import React, { CSSProperties, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { selectCurrentChatId, selectCurrentWaChat } from "./redux/TeamInboxSelectors";
import { selectUser } from "@common/reducers/UserSelectors";
import { accessFormPopupActions } from "@common/reducers/AccessFormPopupReducer";
import { addScheduledMessageButtonActions } from "@common/reducers/AddScheduledMessageButtonReducer";
import { scheduledMessageEditorActions } from "@common/reducers/ScheduledMessageEditorReducer";
import { MessageState } from "@common/types/interface";
import { ExtendedUserMessage } from "@common/models/extend-user-message";
import { getWaChatContactDisplayName } from "@/common/utils/display-name-utils";
import moment from "moment";

/**
 * Team Inbox version of AddScheduledMessageButton
 * This version doesn't use the handleClickScheduleMessageThunk because it has
 * extension-specific code that fails in Team Inbox context (getMessageFromInputField)
 */
export const TeamInboxScheduleButton: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [isHovered, setIsHovered] = useState(false);

    // Get the current chat ID and chat data from Team Inbox state
    const currentChatId = useAppSelector(selectCurrentChatId);
    const currentWaChat = useAppSelector(selectCurrentWaChat);

    // Also get the WhatsApp currentChat to use for the schedule message
    const whatsappCurrentChat = useAppSelector(state => state.WhatsAppReducer?.currentChat);

    // Check if user is logged in
    const user = useAppSelector(selectUser);

    // Button is disabled only if no chat is selected
    const isDisabled = !currentChatId;

    const handleClickOpen = async () => {
        console.log('[TeamInboxScheduleButton] ========== BUTTON CLICKED ==========');
        console.log('[TeamInboxScheduleButton] currentChatId:', currentChatId);
        console.log('[TeamInboxScheduleButton] currentWaChat:', currentWaChat);
        console.log('[TeamInboxScheduleButton] whatsappCurrentChat:', whatsappCurrentChat);
        console.log('[TeamInboxScheduleButton] user:', user ? { _id: user._id, email: user.email } : null);

        if (!currentChatId) {
            console.log('[TeamInboxScheduleButton] No chat selected, returning');
            return;
        }

        // Check if user is logged in
        if (!user) {
            console.log('[TeamInboxScheduleButton] User not logged in, showing login popup');
            dispatch(accessFormPopupActions.setStatus('Login'));
            return;
        }

        // Get the chat to use - prefer whatsappCurrentChat (set when clicking chat), fall back to currentWaChat
        const chatForSchedule = whatsappCurrentChat || currentWaChat;

        if (!chatForSchedule) {
            console.warn('[TeamInboxScheduleButton] No chat data available');
            return;
        }

        console.log('[TeamInboxScheduleButton] Chat for schedule:', {
            id: chatForSchedule.id?._serialized || chatForSchedule._id,
            name: chatForSchedule.name
        });

        // Create the message object for the schedule dialog
        // This is the same logic as handleClickScheduleMessageThunk but without extension-specific code.
        // _id is intentionally omitted — the editor creates one on schedule. Widened via cast below.
        const message = {
            dueDate: moment().toDate(),
            message: "", // In Team Inbox, we don't try to get message from WhatsApp input field
            isRecurring: false,
            state: MessageState.pending,
            cancelIfReceived: false,
            isTimeValid: true,
            contactList: [
                {
                    ...chatForSchedule,
                    id: chatForSchedule.id?._serialized || chatForSchedule._id || currentChatId,
                    name: getWaChatContactDisplayName(chatForSchedule) || chatForSchedule.name || currentChatId,
                },
            ],
        } as ExtendedUserMessage;

        console.log('[TeamInboxScheduleButton] Created message:', message);

        // Dispatch the actions to open the schedule dialog
        dispatch(scheduledMessageEditorActions.loadMessageData(message));
        dispatch(addScheduledMessageButtonActions.editMessage(message));

        console.log('[TeamInboxScheduleButton] Actions dispatched, dialog should open');
        console.log('[TeamInboxScheduleButton] ========== END ==========');
    };

    const baseButtonStyle = React.useMemo<CSSProperties>(() => ({
        border: 'none',
        background: 'transparent',
        width: 40,
        height: 40,
        borderRadius: '50%',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    }), []);

    return (
        <Tooltip title={isDisabled ? t('teamInbox.selectChatToSchedule') : t('teamInbox.scheduleMessage')} arrow placement="top">
            <span> {/* Wrapper span needed for Tooltip when button is disabled */}
                <IconButton
                    onClick={handleClickOpen}
                    disabled={isDisabled}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    sx={{
                        ...baseButtonStyle,
                        color: 'var(--wa-secondary-text)',
                        opacity: isDisabled ? 0.5 : 1,
                        '&:hover': {
                            backgroundColor: 'var(--wa-hover-action-bg)'
                        }
                    }}
                    aria-label={t('teamInbox.scheduleMessageAria', 'Schedule a message')}
                >
                    {isHovered && !isDisabled ? <AccessTimeFilledIcon /> : <AccessTimeIcon />}
                </IconButton>
            </span>
        </Tooltip>
    );
};

export default TeamInboxScheduleButton;
